import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, 
  X, 
  Check, 
  Lock, 
  SlidersHorizontal, 
  BarChart3, 
  ChevronDown, 
  ChevronUp,
  ShieldAlert
} from 'lucide-react';
import KSButton from '../common/KSButton';
import { 
  getStoredConsent, 
  saveConsent as persistConsent, 
  isGpcEnabled 
} from '../../services/consentService';

export default function DataConsentModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    functional: true,
    analytics: true,
  });

  const gpcActive = isGpcEnabled();

  // Check consent status on initial load
  useEffect(() => {
    try {
      const stored = getStoredConsent();
      if (stored) {
        setPreferences({
          essential: true,
          functional: Boolean(stored.functional),
          analytics: Boolean(stored.analytics),
        });
      } else {
        // If GPC signal is active on unconsented visit, default analytics to off
        if (gpcActive) {
          setPreferences(prev => ({ ...prev, analytics: false }));
        }

        // Delay popup slightly after initial route mount for smoother, non-jarring UX
        const timer = setTimeout(() => {
          setIsOpen(true);
        }, 700);
        return () => clearTimeout(timer);
      }
    } catch (err) {
      console.warn('[DataConsentModal] Error verifying initial consent:', err);
    }
  }, [gpcActive]);

  // Listen for custom event to reopen preferences anytime (from Footer, Privacy page, or Settings)
  useEffect(() => {
    const handleReopen = () => {
      // Refresh current preferences from storage
      const current = getStoredConsent();
      if (current) {
        setPreferences({
          essential: true,
          functional: Boolean(current.functional),
          analytics: Boolean(current.analytics),
        });
      }
      setIsOpen(true);
      setShowPreferences(false);
    };

    window.addEventListener('ks:open-consent-modal', handleReopen);
    return () => window.removeEventListener('ks:open-consent-modal', handleReopen);
  }, []);

  const handleSave = (prefs) => {
    const result = persistConsent(prefs);
    if (result) {
      setPreferences(result);
    }
    setIsOpen(false);
    setShowPreferences(false);
  };

  const handleAcceptAll = () => {
    handleSave({ functional: true, analytics: !gpcActive });
  };

  const handleEssentialOnly = () => {
    handleSave({ functional: false, analytics: false });
  };

  const handleSavePreferences = () => {
    handleSave(preferences);
  };

  const handleDismiss = () => {
    // Dismissing without explicit choice defaults to essential-only for privacy compliance
    handleEssentialOnly();
  };

  if (!isOpen) return null;

  return (
    <aside 
      aria-label="Privacy & Cookie Consent Notification"
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 md:bottom-8 md:right-8 z-[99990] animate-slide-in-right pointer-events-auto max-w-[calc(100vw-2rem)] sm:max-w-[520px] w-full"
    >
      <div className="relative flex flex-col bg-[#363431] text-white rounded-xl shadow-2xl border-2 border-[#4a4743] overflow-visible">
        {/* Floating Top-Right Close Button */}
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute -top-3 -right-3 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#211f1e] hover:bg-[#2c2a28] text-stone-400 hover:text-white flex items-center justify-center border-2 border-[#4a4743] shadow-lg cursor-pointer z-20 transition-all hover:scale-110 active:scale-95"
          aria-label="Close and accept essential only"
          title="Accept essential cookies only"
        >
          <X size={16} className="stroke-[2.5]" />
        </button>

        {/* Main Banner Header / Summary */}
        <div className="flex flex-col sm:flex-row items-stretch">
          {/* Left Themed Icon Box */}
          <div className="w-full sm:w-28 bg-gradient-to-b from-[#2d2a28] to-[#211f1e] flex flex-row sm:flex-col items-center justify-center p-3.5 sm:p-2 shrink-0 rounded-t-xl sm:rounded-t-none sm:rounded-l-xl border-b sm:border-b-0 sm:border-r-2 border-[#4a4743] relative overflow-hidden">
            {/* Subtle Ambient Amber Glow */}
            <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/25 via-orange-500/15 to-transparent pointer-events-none" />

            <div className="relative flex items-center justify-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shadow-inner">
                <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8 text-amber-400 drop-shadow-sm" />
              </div>
            </div>

            <span className="ml-3 sm:ml-0 sm:mt-1.5 text-[11px] font-black tracking-wider text-amber-300 uppercase drop-shadow z-10">
              PRIVACY
            </span>
          </div>

          {/* Right Text Content */}
          <div className="px-4 sm:px-5 py-3.5 flex-1 min-w-0 flex flex-col justify-center">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
              <h3 className="font-black text-base sm:text-lg text-white leading-tight flex items-center gap-1.5">
                We Value Your Privacy
              </h3>
              <div className="flex items-center gap-1.5 flex-wrap">
                {gpcActive && (
                  <span className="text-[10px] font-bold text-amber-300 bg-amber-500/20 px-2 py-0.5 rounded border border-amber-500/30 flex items-center gap-1">
                    <ShieldAlert size={11} />
                    GPC Active
                  </span>
                )}
                <span className="text-[10px] font-bold text-stone-400 bg-black/30 px-2 py-0.5 rounded border border-stone-700">
                  GDPR & CCPA
                </span>
              </div>
            </div>

            <p className="text-xs sm:text-[13px] text-stone-300 font-medium leading-relaxed">
              KnightSteps uses essential cookies and local data to secure your session, remember your chessboard themes, and optimize performance.{' '}
              <Link 
                to="/privacy#cookies" 
                className="text-amber-400 hover:text-amber-300 underline font-semibold transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Learn more
              </Link>
            </p>
          </div>
        </div>

        {/* Granular Preference Accordion */}
        {showPreferences && (
          <div className="px-4 sm:px-5 py-3 border-t border-[#4a4743] bg-[#292725]/90 space-y-2.5 animate-fade-in">
            <div className="flex items-center justify-between pb-1 border-b border-stone-700/50">
              <span className="text-xs font-bold text-stone-300 uppercase tracking-wider">
                Cookie & Data Categories
              </span>
              <span className="text-[11px] text-stone-400">Custom Preferences</span>
            </div>

            {/* 1. Essential */}
            <div className="flex items-start justify-between gap-3 p-2.5 rounded-lg bg-black/25 border border-stone-700/60">
              <div className="flex items-start gap-2.5 min-w-0">
                <Lock size={16} className="text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-white">Strictly Necessary</span>
                    <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                      Required
                    </span>
                  </div>
                  <p className="text-[11px] text-stone-400 mt-0.5 leading-snug">
                    Session authentication, JWT security tokens, and CSRF protection. Cannot be deactivated.
                  </p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={true}
                disabled={true}
                className="w-4 h-4 rounded mt-1 accent-amber-500 opacity-80 cursor-not-allowed"
                aria-label="Essential cookies cannot be disabled"
              />
            </div>

            {/* 2. Functional & Board Preferences */}
            <label className="flex items-start justify-between gap-3 p-2.5 rounded-lg bg-black/25 border border-stone-700/60 hover:bg-black/35 cursor-pointer transition-colors">
              <div className="flex items-start gap-2.5 min-w-0">
                <SlidersHorizontal size={16} className="text-blue-400 mt-0.5 shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-white">Functional & Chess Preferences</span>
                  </div>
                  <p className="text-[11px] text-stone-400 mt-0.5 leading-snug">
                    Remembers board piece sets, custom board themes, move sound toggles, and coordinate display.
                  </p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={preferences.functional}
                onChange={(e) => setPreferences(prev => ({ ...prev, functional: e.target.checked }))}
                className="w-4 h-4 rounded mt-1 accent-amber-500 cursor-pointer"
                aria-label="Toggle Functional and Chess Preferences"
              />
            </label>

            {/* 3. Analytics & Performance */}
            <label className="flex items-start justify-between gap-3 p-2.5 rounded-lg bg-black/25 border border-stone-700/60 hover:bg-black/35 cursor-pointer transition-colors">
              <div className="flex items-start gap-2.5 min-w-0">
                <BarChart3 size={16} className="text-purple-400 mt-0.5 shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-white">Analytics & Performance</span>
                    {gpcActive && (
                      <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                        Restricted by GPC
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-stone-400 mt-0.5 leading-snug">
                    Aggregated, anonymous metrics to detect move latency, puzzle loading speed, and UI bugs.
                  </p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={preferences.analytics}
                onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                className="w-4 h-4 rounded mt-1 accent-amber-500 cursor-pointer"
                aria-label="Toggle Analytics and Performance"
              />
            </label>
          </div>
        )}

        {/* Action Button Bar */}
        <div className="px-4 sm:px-5 py-3 bg-[#2a2826] rounded-b-xl border-t border-[#4a4743] flex flex-col sm:flex-row items-center justify-between gap-2.5">
          {/* Preferences Toggle Link */}
          <button
            type="button"
            onClick={() => setShowPreferences(!showPreferences)}
            className="text-xs text-stone-400 hover:text-stone-200 font-semibold flex items-center gap-1 transition-colors py-1 cursor-pointer order-2 sm:order-1"
          >
            {showPreferences ? (
              <>
                <ChevronUp size={15} />
                <span>Hide Preferences</span>
              </>
            ) : (
              <>
                <ChevronDown size={15} />
                <span>Customize Preferences</span>
              </>
            )}
          </button>

          {/* Decision Buttons */}
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end order-1 sm:order-2">
            {showPreferences ? (
              <>
                <KSButton
                  color="stone"
                  textColor="#e7e5e4"
                  width="auto"
                  height="42px"
                  onClick={handleEssentialOnly}
                  title="Reject optional cookies"
                  className="px-3 sm:px-4 text-xs font-bold flex-1 sm:flex-initial"
                >
                  Reject All
                </KSButton>
                <KSButton
                  color="yellow"
                  textColor="#1c1917"
                  width="auto"
                  height="42px"
                  onClick={handleSavePreferences}
                  title="Save selected preferences"
                  className="px-4 text-xs font-black flex-1 sm:flex-initial"
                >
                  <Check size={16} className="stroke-[3]" />
                  <span>Save Choices</span>
                </KSButton>
              </>
            ) : (
              <>
                <KSButton
                  color="stone"
                  textColor="#e7e5e4"
                  width="auto"
                  height="42px"
                  onClick={handleEssentialOnly}
                  title="Accept essential cookies only"
                  className="px-3 sm:px-4 text-xs font-bold flex-1 sm:flex-initial"
                >
                  Essential Only
                </KSButton>
                <KSButton
                  color="yellow"
                  textColor="#1c1917"
                  width="auto"
                  height="42px"
                  onClick={handleAcceptAll}
                  title="Accept all cookies and preferences"
                  className="px-4 text-xs font-black flex-1 sm:flex-initial"
                >
                  <Check size={16} className="stroke-[3]" />
                  <span>Accept All</span>
                </KSButton>
              </>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}
